import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_PROJECT_URL,
  process.env.REACT_APP_PUBLIC_ANON_KEY,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
);

let currentSession = null;
const listeners = new Set();
let initialized = false;

// Set session and notify listeners
const setSession = (session) => {
  currentSession = session;
  notifyListeners();
};

// Get the current session
const getSession = () => {
  return currentSession;
};

// Initialize session and setup auth listener once
const initSession = async () => {
  if (initialized) return;
  initialized = true;

  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error("Error getting session:", error.message);
      return;
    }

    if (session) {
      // Verify email authorization
      const isAuthorized = await isAuthorizedEmail(session.user.email);
      if (!isAuthorized) {
        await supabase.auth.signOut();
        window.location.href = '/login?error=unauthorized';
        return;
      }
      setSession(session);
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        const isAuthorized = await isAuthorizedEmail(session.user.email);
        if (!isAuthorized) {
          await supabase.auth.signOut();
          window.location.href = '/login?error=unauthorized';
          return;
        }
      }
      setSession(session);
    });
  } catch (error) {
    console.error("Session init error:", error);
  }
};

// Check if email is authorized from the database
const isAuthorizedEmail = async (email) => {
  try {
    const { data, error } = await supabase
      .from('login')
      .select('email, role')
      .eq('email', email)
      .single();

    if (error) {
      console.error('Error checking authorized email:', error.message);
      return false;
    }

    return data && data.role === 'admin';
  } catch (error) {
    console.error('Error in isAuthorizedEmail:', error.message);
    return false;
  }
};

// Google login handler
const loginWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      console.error('Error logging in with Google:', error.message);
      window.location.href = '/login?error=google-auth-failed';
      return null;
    }

    // Get the session immediately after OAuth
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      throw sessionError;
    }

    // Check if we have a session and user email
    if (session?.user?.email) {
      // Verify email authorization
      const isAuthorized = await isAuthorizedEmail(session.user.email);
      if (!isAuthorized) {
        await supabase.auth.signOut();
        window.location.href = '/login?error=unauthorized';
        return null;
      }
      setSession(session);
      return session;
    }

    return data;
  } catch (error) {
    console.error('Login failed:', error.message);
    window.location.href = '/login?error=google-auth-failed';
    return null;
  }
};

// Add a listener for session updates
const addListener = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Notify all listeners of session change
const notifyListeners = () => {
  listeners.forEach((listener) => listener(currentSession));
};

export {
  supabase,
  initSession,
  getSession,
  loginWithGoogle,
  addListener,
  isAuthorizedEmail,
};
